import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CardComponentDataProps } from '../../cards/dividedCard/DividedCard';
import { ellipsify } from '../../../../typography/ellipsify';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { useTheme } from '../../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { Link } from '../../link/Link';

const createStyles = (
  borderColor: string,
  linksColor: string,
  linkHoverColor: string,
  vrLineHeight: VrLineHeight,
) => StyleSheet.create({
  sectionItem: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      /*
        width is calculation of 50% - margins. The list is 200% width
        therefore to fit the visual container we set the item width to 50%
       */
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`,
    },
    borderTop: `1px solid ${borderColor}`,
    flex: '0 0 auto',
    boxSizing: 'border-box',
    position: 'relative',
    ':first-child': {
      borderTop: 'none',
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none',
      },
    },
  },
  link: {
    textDecoration: 'none',
    boxSizing: 'border-box',
    color: linksColor,
    ':visited': {
      color: linksColor,
      textDecoration: 'none',
    },
    ':active': {
      color: linksColor,
      textDecoration: 'none',
    },
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none',
      },
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none',
      },
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial',
      },
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      '-webkitLineClamp': '2',
      ':hover': {
        color: 'initial',
      },
    },
  },
});

export interface ListItemComponentThemeProps {
  borderColor: string
  linkColor: string
  linkHoverColor: string
  vrLineHeight: VrLineHeight
}

const componentConfigurationKey = 'listItem';
export type ListItemProps = ListItemComponentThemeProps & CardComponentDataProps;

export const ListItemComponent: React.FunctionComponent<ListItemProps> = (
  {
    borderColor,
    linkColor,
    linkHoverColor,
    vrLineHeight,
    title,
    articleUrl,
    numberOfLines = 2,
  },
) => {
  const styles = createStyles(
    borderColor,
    linkColor,
    linkHoverColor,
    vrLineHeight,
  );
  // @ts-ignore TS2345
  const { style } = StyleSheet.create(ellipsify(numberOfLines));
  return (
    <Link
      href={articleUrl}
      className={css(
        styles.link,
        styles.sectionItem,
      )}
      key={articleUrl}
    >
      <h4 className={css(style)}>{title}</h4>
    </Link>
  );
};

const mapThemeProps = (theme: ListItemComponentThemeProps) => ({
  borderColor: theme.borderColor,
  linkColor: theme.linkColor,
  linkHoverColor: theme.linkHoverColor,
  vrLineHeight: theme.vrLineHeight,
});

export const ListItem = (props: CardComponentDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return <ListItemComponent {...themeContext} {...props} />;
};
